import { useState } from 'react';
import Page from '@/components/Page';
import { Card, Button, Modal, Tabs, TabPanel } from '@/components/ui';
import { useAppSelector } from '@/store';
import type { RootState } from '@/store';
import UpdatePwdForm from '@/components/profile/UpdatePwdForm';
import ConfigCard from '@/components/users/ConfigCard';
import { KeyRound, User, Settings } from 'lucide-react';

function getInitials(user: RootState['auth']['user']): string {
  const nom = user?.nom ?? '';
  const prenom = user?.prenom ?? '';
  if (prenom || nom) return `${(prenom as string)[0] ?? ''}${(nom as string)[0] ?? ''}`.toUpperCase();
  const u = user?.username ?? user?.login ?? '';
  return u.slice(0, 2).toUpperCase() || '?';
}

export default function ProfilePage() {
  const user = useAppSelector((s: RootState) => s.auth.user);
  const isAdmin = useAppSelector((s: RootState) => s.auth.isAdmin);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [tab, setTab] = useState(0);

  const displayName = [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.username || user?.login || '—';

  const infoRows: { label: string; value: string }[] = [
    { label: 'Login', value: user?.login ?? '—' },
    { label: "Nom d'utilisateur", value: user?.username ?? user?.login ?? '—' },
    { label: 'Prénom', value: user?.prenom ?? '—' },
    { label: 'Nom', value: user?.nom ?? '—' },
    {
      label: 'Rôles',
      value: (user?.roles?.map((r) => r.name ?? r.nom ?? r.code).filter(Boolean).join(', ') as string) || '—',
    },
  ];

  return (
    <Page title="Profil">
      <div className="space-y-6">
        {/* Profile header */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xl font-semibold text-teal-700">
              {getInitials(user)}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-semibold text-slate-900">{displayName}</h2>
              {user?.login && (
                <p className="mt-0.5 truncate text-sm text-slate-500">{user.login}</p>
              )}
            </div>
          </div>
          <div className="mt-4 border-t border-slate-100 pt-4">
            <Tabs
              tabs={[
                { label: 'Profil', icon: <User className="h-4 w-4" /> },
                { label: 'Configuration', icon: <Settings className="h-4 w-4" />, disabled: !isAdmin },
              ]}
              value={tab}
              onChange={setTab}
            />
          </div>
        </div>

        <TabPanel value={tab} index={0}>
          <div className="space-y-6">
            {/* Info card */}
            <Card title="Informations du compte">
              <div className="grid gap-4 sm:grid-cols-2">
                {infoRows.map(({ label, value }) => (
                  <div key={label} className="rounded-lg bg-slate-50/80 px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
                    <p className="mt-1 truncate text-sm font-medium text-slate-900">{value || '—'}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Security */}
            <Card title="Sécurité">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                    <KeyRound className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Mot de passe</p>
                    <p className="text-xs text-slate-500">Modifiez votre mot de passe de connexion</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPwdModalOpen(true)} className="shrink-0">
                  <KeyRound className="mr-2 h-4 w-4" />
                  Modifier le mot de passe
                </Button>
              </div>
            </Card>
          </div>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <ConfigCard />
        </TabPanel>
      </div>

      <Modal open={pwdModalOpen} onClose={() => setPwdModalOpen(false)} title="Modifier le mot de passe">
        <UpdatePwdForm onClose={() => setPwdModalOpen(false)} onSuccess={() => setPwdModalOpen(false)} />
      </Modal>
    </Page>
  );
}
